import useOnClickOutside from '@/core/listeners/useOnClickOutside';
import { useTracker } from '@/core/tracking';
import { useFormatService } from '@/layout/appWrapper/ServiceProvider';
import { Format } from '@/services/formatService/formatTypes';
import { useLocalization } from '@/services/localizationService';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useRef, useState } from 'react';

import crown from '../../../public/images/icons/crown.svg';
import downArrowIcon from '../../../public/images/icons/down-small--format.svg';
import upArrowIcon from '../../../public/images/icons/up-small--format.svg';

export default function FormatDropdown({
  downloadFormat,
  shouldShowMultipleFormatsLabel,
  onFormatSelection,
}: {
  downloadFormat: Format;
  shouldShowMultipleFormatsLabel: boolean;
  onFormatSelection: (format: Format) => void;
}) {
  const { getAllFormats } = useFormatService();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const dropdownRef = useRef(null);
  useOnClickOutside(
    dropdownRef,
    [],
    useCallback(() => setIsOpen(false), [setIsOpen])
  );

  const { track } = useTracker();
  const { t, data } = useLocalization();

  return (
    <div ref={dropdownRef}>
      <div className="mt-3 w-full bg-overlay-white-10 border-none rounded-lg">
        <button
          type="button"
          className="SB12 bg-transparent text-left text-white w-full h-8 px-3 flex items-center justify-between"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded="true"
          aria-haspopup="true"
        >
          <span>
            {shouldShowMultipleFormatsLabel
              ? t(data.result.multipleFormats)
              : downloadFormat.label}
          </span>
          {isOpen ? (
            <img {...upArrowIcon} alt="" />
          ) : (
            <img {...downArrowIcon} alt="" />
          )}
        </button>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="format-dropdown"
            className="mt-2 w-full bg-overlay-white-5 border-none rounded-lg"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'easeInOut', duration: 0.1 }}
            aria-orientation="vertical"
            aria-labelledby="menu-button"
          >
            <ul>
              {getAllFormats().map((format) => {
                if (
                  format.label === downloadFormat.label &&
                  !shouldShowMultipleFormatsLabel
                ) {
                  return;
                }

                return (
                  <li key={format.label}>
                    <button
                      className="SB12 py-2.5 px-3 w-full flex items-center justify-between text-gray-400 text-left hover:bg-overlay-white-5 hover:border-none hover:rounded-lg hover:text-gray-200"
                      onClick={() => {
                        setIsOpen(!isOpen);
                        onFormatSelection(format);

                        track('output_format', 'changed', {
                          desiredFormat: format.label,
                        });
                      }}
                    >
                      <span>{format.label}</span>
                      {format.isBusinessOnly && <img {...crown} alt="" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
